if (!customElements.get('vh-home-banner')) {
  customElements.define('vh-home-banner', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.index = 0;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.inView = true;
      this.editorSelected = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.slides.length < 2) return;
      this.querySelectorAll('[data-controls]').forEach(control => { control.hidden = false; });
      this.setAttribute('data-ready', '');
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-slide-to')) this.show(Number(button.dataset.slideTo), true);
        if (button.hasAttribute('data-previous')) this.show(this.index - 1, true);
        if (button.hasAttribute('data-next')) this.show(this.index + 1, true);
        if (button.hasAttribute('data-pause')) {
          this.paused = !this.paused;
          button.setAttribute('aria-pressed', String(this.paused));
          button.setAttribute('aria-label', this.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
          button.querySelector('[data-pause-icon]').hidden = this.paused;
          button.querySelector('[data-play-icon]').hidden = !this.paused;
          this.schedule();
        }
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      }, { signal });
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, { signal });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, { signal });
      this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); this.schedule(); }, { signal });
      this.addEventListener('touchstart', event => {
        this.touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        clearTimeout(this.timer);
      }, { passive: true, signal });
      this.addEventListener('touchend', event => {
        if (!this.touch) return;
        const x = event.changedTouches[0].clientX - this.touch.x;
        const y = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(x) > 50 && Math.abs(x) > Math.abs(y) * 1.5) this.show(this.index + (x < 0 ? 1 : -1), true);
        this.touch = null;
        this.schedule();
      }, { passive: true, signal });
      this.addEventListener('touchcancel', () => { this.touch = null; this.schedule(); }, { passive: true, signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => this.schedule(), { signal });
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.show(index, false);
      }, { signal });
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.slides.some(slide => slide.dataset.blockId === event.detail.blockId)) return;
        this.editorSelected = false;
        this.schedule();
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.1 });
      this.observer.observe(this);
      this.schedule();
    }
    show(index, manual) {
      const next = (index + this.slides.length) % this.slides.length;
      const active = document.activeElement;
      const moveFocus = this.slides[this.index].contains(active);
      const topicIndex = active?.dataset?.slideTo;
      this.index = next;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== next;
        slide.inert = i !== next;
      });
      this.querySelectorAll('[data-slide-to]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.slideTo) === next)));
      if (moveFocus) {
        const target = this.slides[next].querySelector(`[data-slide-to="${topicIndex ?? next}"]`);
        target?.focus({ preventScroll: true });
      }
      this.querySelector('[data-slides]').setAttribute('aria-live', manual ? 'polite' : 'off');
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.dataset.autoplay !== 'true' || this.paused || this.hovered || this.focused || this.motion.matches || document.hidden || !this.inView || this.editorSelected) return;
      this.timer = setTimeout(() => this.show(this.index + 1, false), Number(this.dataset.interval) || 6000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
